import { Check, Copy, ExternalLink, Share2, Smartphone, WifiOff } from 'lucide-react'
import { lazy, Suspense, useState } from 'react'

import { Button, Card, Sheet } from '../../components/ui'
import { activatePwaUpdate, usePwaLifecycle } from '../../services/pwaLifecycle'
import { usePwa } from './pwaContext'

const QRCode = lazy(async () => ({ default: (await import('qrcode.react')).QRCodeSVG }))

async function copyText(value: string): Promise<void> {
  const candidate: unknown = Reflect.get(navigator, 'clipboard')
  if (!candidate || typeof candidate !== 'object') throw new Error('Copie indisponible.')
  const writeText: unknown = Reflect.get(candidate, 'writeText')
  if (typeof writeText !== 'function') throw new Error('Copie indisponible.')
  await Reflect.apply(writeText, candidate, [value])
}

export function InstallCaravanSheet() {
  const { closeInstaller, environment, installChoice, installNow, isLocalOrigin, origin } = usePwa()
  const lifecycle = usePwaLifecycle()
  const [actionMessage, setActionMessage] = useState<string | null>(null)
  const canShare = typeof navigator.share === 'function'

  async function copyOrigin(): Promise<void> {
    try {
      await copyText(origin)
      setActionMessage('Lien copié.')
    } catch {
      setActionMessage('Copie automatique indisponible. Sélectionnez le lien ci-dessus.')
    }
  }

  async function shareOrigin(): Promise<void> {
    if (!canShare) return
    try {
      await navigator.share({ title: 'Caravan', text: 'Ouvrir Caravan', url: origin })
      setActionMessage('Lien partagé.')
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return
      setActionMessage('Partage interrompu. Vous pouvez copier le lien.')
    }
  }

  return (
    <Sheet title="Installer Caravan" onClose={closeInstaller}>
      <div className="install-sheet">
        <Card className="install-guide">
          <Smartphone size={22} aria-hidden="true" />
          <div>
            {environment === 'standalone' ? (
              <><h3>Caravan est déjà installée</h3><p>Tu peux l’ouvrir depuis son icône.</p></>
            ) : null}
            {environment === 'chromium-prompt' ? (
              <>
                <h3>Installer sur Android</h3>
                <p>Ajoute Caravan à ton écran d’accueil.</p>
                <Button onClick={() => void installNow()}>Installer sur cet appareil</Button>
              </>
            ) : null}
            {environment === 'ios-safari' ? (
              <>
                <h3>Installer sur iPhone ou iPad</h3>
                <ol>
                  <li>Ouvre cette page dans Safari.</li>
                  <li>Touche le bouton Partager.</li>
                  <li>Choisis « Sur l’écran d’accueil ».</li>
                  <li>Confirme l’ajout.</li>
                </ol>
              </>
            ) : null}
            {environment === 'ios-embedded' ? (
              <>
                <h3>Continuer dans Safari</h3>
                <p>Ouvre le lien dans Safari, puis utilise Partager et « Sur l’écran d’accueil ».</p>
              </>
            ) : null}
            {environment === 'unavailable' ? (
              <>
                <h3>Installation non proposée ici</h3>
                <p>Garde le lien ou ouvre-le avec Chrome sur Android ou Safari sur iPhone/iPad.</p>
              </>
            ) : null}
            {installChoice === 'accepted' ? <p className="install-result" role="status"><Check size={16} /> Installation acceptée. L’icône va apparaître sur l’appareil.</p> : null}
            {installChoice === 'dismissed' ? <p className="install-result" role="status">Installation différée. Caravan ne vous sollicitera pas de nouveau pendant cette visite.</p> : null}
          </div>
        </Card>

        <section className="install-share" aria-labelledby="install-share-title">
          <div>
            <p className="eyebrow">SUR UN AUTRE TÉLÉPHONE</p>
            <h3 id="install-share-title">Scanner l’origine Caravan</h3>
          </div>
          <div className="install-qr" data-testid="install-qr" data-value={origin}>
            <Suspense fallback={<span className="install-qr__loading">Préparation du QR…</span>}>
              <QRCode
                bgColor="#ffffff"
                fgColor="#17324d"
                level="M"
                marginSize={4}
                size={216}
                title={`QR vers ${origin}`}
                value={origin}
              />
            </Suspense>
          </div>
          <a className="install-origin" href={origin}><ExternalLink size={15} /> {origin}</a>
          {isLocalOrigin ? <p className="install-local-warning"><WifiOff size={16} /> Sur localhost, ce QR ne fonctionnera généralement pas depuis un autre téléphone.</p> : null}
          <div className="install-share__actions">
            <Button variant="secondary" onClick={() => void copyOrigin()}><Copy size={16} /> Copier le lien</Button>
            {canShare ? <Button variant="secondary" onClick={() => void shareOrigin()}><Share2 size={16} /> Partager</Button> : null}
          </div>
          {!canShare ? <p className="install-share__fallback">Le partage natif n’est pas disponible ici. Utilisez « Copier le lien ».</p> : null}
          {actionMessage ? <p className="install-result" role="status">{actionMessage}</p> : null}
        </section>

        {lifecycle.updateAvailable ? (
          <Card className="install-update">
            <h3>Une nouvelle version est prête</h3>
            <p>Choisis quand l’installer. Tes contributions restent conservées.</p>
            <Button disabled={lifecycle.updating} onClick={() => void activatePwaUpdate()}>{lifecycle.updating ? 'Mise à jour…' : 'Mettre à jour'}</Button>
          </Card>
        ) : null}
      </div>
    </Sheet>
  )
}
